import { z } from "zod";
import { AcquisitionFieldRequestSchema } from "./evidence-acquisition";

/**
 * `GoalTask`：目标 → 阶段 → 子任务的**通用**节点（方案 §4.1 L1）。
 *
 * 现状是 `product-launch-task.ts` 的 8 个扁平 step 与 `marketSignals` 的四维扁平项，
 * 没有承载"某个子任务要什么、做完算不算完成、该去哪里做"的地方。这里只**包一层**，
 * 不替换既有阶段机：默认拆解仍用现有 8 步 + 四维，只允许在"初筛"下增补子任务。
 *
 * 两个关键约束：
 * 1. `deliverable.fields` 直接就是 `AcquisitionFieldRequest` 的形状，
 *    所以"子任务 → 取数目标"是**一步映射**，不需要新概念；
 * 2. `approachHints` 只是思路提示（"市场需求可去 Google Trends"），**不是站点准入** ——
 *    某个提示指向从未适配过的站点完全合法，准入由导航层判定。
 *
 * 边界：`evidenceRefs` / `recipeRef` 只是**关联**，不构成任何执行权限。
 */

export const GoalTaskKindSchema = z.enum(["goal", "stage", "subtask"]);
export type GoalTaskKind = z.infer<typeof GoalTaskKindSchema>;

export const GoalTaskStatusSchema = z.enum(["pending", "in_progress", "partial", "done", "failed"]);
export type GoalTaskStatus = z.infer<typeof GoalTaskStatusSchema>;

export const GoalTaskDeliverableSchema = z.object({
  fields: z.array(AcquisitionFieldRequestSchema).min(1).max(40),
  completionCriteria: z.array(z.string().max(240)).min(1).max(12),
}).strict();
export type GoalTaskDeliverable = z.infer<typeof GoalTaskDeliverableSchema>;

export const GoalTaskApproachHintSchema = z.object({
  note: z.string().min(1).max(240),
  /** 提示性的站点（域名或名字），**不是准入白名单**。 */
  suggestedSite: z.string().min(1).max(240).optional(),
}).strict();
export type GoalTaskApproachHint = z.infer<typeof GoalTaskApproachHintSchema>;

export const GoalTaskSchema = z.object({
  nodeId: z.string().uuid(),
  parentId: z.string().uuid().optional(),
  kind: GoalTaskKindSchema,
  title: z.string().min(1).max(160),
  objective: z.string().min(1).max(300),
  deliverable: GoalTaskDeliverableSchema,
  approachHints: z.array(GoalTaskApproachHintSchema).max(6).default([]),
  status: GoalTaskStatusSchema,
  /** 证据与链路的**关联**（bundleId / sessionId / recipeId），不授予任何权限。 */
  evidenceRefs: z.array(z.string().min(1).max(160)).max(60).default([]),
  recipeRef: z.string().min(1).max(160).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).strict().superRefine((value, context) => {
  // 只有 goal 是根：其余节点必须挂在某个父节点下，否则树是断的。
  if (value.kind !== "goal" && !value.parentId) {
    context.addIssue({ code: "custom", path: ["parentId"], message: "阶段与子任务必须指定父节点" });
  }
});
export type GoalTask = z.infer<typeof GoalTaskSchema>;

/**
 * 这个节点现在能不能派发取数。
 * 只有**带齐交付物字段与完成判据**的子任务才算可执行 —— 否则"做完了没有"无从判断，
 * 会出现"点了按钮、拿到了数据、但不知道算不算完成"的状态。
 */
export function isExecutableSubtask(node: GoalTask): boolean {
  return node.kind === "subtask" && node.deliverable.fields.length > 0 && node.deliverable.completionCriteria.length > 0
    && ["pending", "in_progress", "partial"].includes(node.status);
}
